import { useState, FC, useEffect, useCallback } from "react";
// Removed Account.css import, styles will be handled by Tailwind
import React from "react";
// Removed unused Header import
import baseApi from "utils/axios-instance"; // Import baseApi
// Import all AccountInfo dependencies
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Mixpanel } from "utils/Analytics";
import { OnboardingInfoSchema } from "utils/validationSchema";
import { useSelector, useDispatch } from "react-redux";
import { ACCOUNTSAVED } from "utils/reducers";
import { SPECIALTY_OPTIONS } from "utils/constants";
import {
  Archive,
  Loader2,
  User,
  Building2,
  Stethoscope,
  Settings,
  UserPlus,
} from "lucide-react";
// Shadcn/UI component imports
import { Button } from "components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "components/ui/card";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Toaster } from "components/ui/sonner";
import { toast } from "sonner";
import NewPracticeDialog from "../NewRequestPage/components/NewPracticeDialog";

interface AccountProps {
  refreshProfileInfo: () => void;
}

// Removed unused UserType interface

const Account: FC<AccountProps> = ({ refreshProfileInfo }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showNewProviderDialog, setShowNewProviderDialog] = useState(false);
  const [selectedPracticeId, setSelectedPracticeId] = useState("");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [practiceToArchive, setPracticeToArchive] = useState<any>(null);
  const [archiving, setArchiving] = useState(false);

  const showAccountSaved = useSelector(
    (state: any) => state.showAccountSaved.show
  );
  const dispatch = useDispatch();

  const form = useForm({
    resolver: yupResolver(OnboardingInfoSchema),
    defaultValues: {
      specialty: "",
      practices: [],
    },
  });

  const { control, handleSubmit, watch, setValue } = form;

  const onSubmit = async (data: any) => {
    setSubmitting(true);
    try {
      await baseApi.post("/onboarding", data, { withCredentials: true });
      Mixpanel.track("Onboarding Updated");
      if (refreshProfileInfo) refreshProfileInfo();
      toast.success("Your account information has been saved.");
    } catch (error: any) {
      Mixpanel.track("Error: Onboarding", { error: error.message });
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "An unexpected error occurred.";
      toast.error(message, {
        description: "Failed to save account information.",
      });
      console.error("Onboarding error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const profileResponse = await baseApi.get("/profile");

      setValue("specialty", profileResponse.data.specialty || "");
      setValue("practices", profileResponse.data.practices || []);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load account data.");
    } finally {
      setLoading(false);
    }
  }, [setValue]);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleArchivePractice = async () => {
    if (!practiceToArchive?.id) return;
    setArchiving(true);
    try {
      await baseApi.post(
        `/dashboard/practice/${practiceToArchive.id}/archive`,
        {},
        { withCredentials: true }
      );
      Mixpanel.track("Practice Archived");
      setPracticeToArchive(null);
      // Indices shift once the practice is gone, so drop the open sections.
      setExpandedItems([]);
      await fetchInitialData();
      if (refreshProfileInfo) refreshProfileInfo();
      toast.success(
        `${practiceToArchive.name || "Practice"} has been archived.`
      );
    } catch (error: any) {
      Mixpanel.track("Error: Practice Archive", { error: error.message });
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "An unexpected error occurred.";
      toast.error(message, {
        description: "Failed to archive practice.",
      });
      console.error("Archive practice error:", error);
    } finally {
      setArchiving(false);
    }
  };

  const practices = watch("practices");

  useEffect(() => {
    if (showAccountSaved) {
      toast.info("Information updated (from redux).");
      dispatch({ type: ACCOUNTSAVED.hide });
    }
  }, [showAccountSaved, dispatch]);

  return (
    <div className="w-full">
      <Toaster richColors position="top-center" />
      <div className="p-6 max-w-4xl mx-auto">
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-3 bg-[#005151] rounded-xl">
              <Settings className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Account Settings
              </h1>
              <p className="text-gray-600 mt-1">
                Manage your practice information and account preferences
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl">
          <div className="space-y-8">
            {loading ? (
              <Card className="border border-gray-200 shadow-sm">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <div className="p-4 bg-[#005151]/10 rounded-full mb-4">
                    <Loader2 className="h-8 w-8 animate-spin text-[#005151]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Loading your account information
                  </h3>
                  <p className="text-gray-600">
                    Please wait while we fetch your practice details...
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Form {...form}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                  {/* Medical Specialty Section */}
                  <Card className="border border-gray-200 shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[#005151]/10 rounded-lg">
                          <Stethoscope className="h-5 w-5 text-[#005151]" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-semibold text-gray-900">
                            Medical Specialty
                          </CardTitle>
                          <CardDescription className="text-gray-600 mt-1">
                            Select your primary area of medical practice
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <FormField
                        control={control}
                        name="specialty"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Medical Specialty
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="w-full h-12 border-gray-200 focus:border-[#005151] focus:ring-[#005151]">
                                  <SelectValue placeholder="Choose your medical specialty" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {SPECIALTY_OPTIONS.map((option: any) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  {/* Practice Information Section */}
                  <Card className="border border-gray-200 shadow-sm">
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[#005151]/10 rounded-lg">
                          <Building2 className="h-5 w-5 text-[#005151]" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-semibold text-gray-900">
                            Practice Information
                          </CardTitle>
                          <CardDescription className="text-gray-600 mt-1">
                            Your practice locations and provider details
                          </CardDescription>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setSelectedPracticeId("OTHER");
                          setShowNewProviderDialog(true);
                        }}
                      >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Add New Practice
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {!practices || practices.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <Building2 className="h-8 w-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            No practice information found
                          </h3>
                          <p className="text-gray-600 mb-6 max-w-md mx-auto">
                            We don't have any practice information on file.
                            Please contact our support team to add your practice
                            details.
                          </p>
                          <Button
                            variant="outline"
                            className="border-[#005151] text-[#005151] hover:bg-[#005151] hover:text-white"
                          >
                            Contact Support
                          </Button>
                        </div>
                      ) : (
                        <Accordion
                          type="multiple"
                          value={expandedItems}
                          onValueChange={setExpandedItems}
                          className="w-full"
                        >
                          {practices?.map((practice: any, index: number) => (
                            <AccordionItem
                              value={`item-${index}`}
                              key={index}
                              className="border border-gray-200 rounded-lg mb-4 px-4"
                            >
                              <AccordionTrigger className="hover:no-underline">
                                <div className="flex items-center space-x-3">
                                  <div className="p-2 bg-[#005151]/10 rounded-lg">
                                    <Building2 className="h-5 w-5 text-[#005151]" />
                                  </div>
                                  <div>
                                    <h4 className="text-lg font-semibold text-gray-900 text-left">
                                      {practice.name || `Practice ${index + 1}`}
                                    </h4>
                                    <p className="text-sm text-gray-500 text-left">
                                      {practice.address}, {practice.city}
                                    </p>
                                  </div>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="space-y-6 pt-4">
                                {/* Practice Basic Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <FormField
                                    control={control}
                                    name={`practices.${index}.name` as any}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel className="text-sm font-medium text-gray-700">
                                          Practice Name
                                        </FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder="e.g., Downtown Medical Clinic"
                                            className="h-12 border-gray-200 focus:border-[#005151] focus:ring-[#005151]"
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={control}
                                    name={`practices.${index}.tax_id` as any}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel className="text-sm font-medium text-gray-700">
                                          Tax ID (EIN)
                                        </FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder="e.g., 12-3456789"
                                            className="h-12 border-gray-200 focus:border-[#005151] focus:ring-[#005151]"
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>

                                {/* Address Information */}
                                <div className="space-y-4">
                                  <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                                    Address Information
                                  </h4>
                                  <FormField
                                    control={control}
                                    name={`practices.${index}.address` as any}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel className="text-sm font-medium text-gray-700">
                                          Street Address
                                        </FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder="e.g., 123 Main Street, Suite 101"
                                            className="h-12 border-gray-200 focus:border-[#005151] focus:ring-[#005151]"
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <FormField
                                      control={control}
                                      name={`practices.${index}.city` as any}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel className="text-sm font-medium text-gray-700">
                                            City
                                          </FormLabel>
                                          <FormControl>
                                            <Input
                                              placeholder="e.g., Los Angeles"
                                              className="h-12 border-gray-200 focus:border-[#005151] focus:ring-[#005151]"
                                              {...field}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={control}
                                      name={`practices.${index}.state` as any}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel className="text-sm font-medium text-gray-700">
                                            State
                                          </FormLabel>
                                          <FormControl>
                                            <Input
                                              placeholder="e.g., CA"
                                              className="h-12 border-gray-200 focus:border-[#005151] focus:ring-[#005151]"
                                              {...field}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={control}
                                      name={`practices.${index}.zip` as any}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel className="text-sm font-medium text-gray-700">
                                            ZIP Code
                                          </FormLabel>
                                          <FormControl>
                                            <Input
                                              placeholder="e.g., 90210"
                                              className="h-12 border-gray-200 focus:border-[#005151] focus:ring-[#005151]"
                                              {...field}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                </div>

                                {/* Healthcare Providers */}
                                {practice.providers &&
                                  practice.providers.length > 0 && (
                                    <div className="border-t pt-6">
                                      <div className="flex items-center space-x-3 mb-6">
                                        <div className="p-2 bg-[#005151]/10 rounded-lg">
                                          <User className="h-5 w-5 text-[#005151]" />
                                        </div>
                                        <div>
                                          <h4 className="text-lg font-semibold text-gray-900">
                                            Healthcare Providers
                                          </h4>
                                          <p className="text-sm text-gray-600">
                                            Licensed providers at this practice
                                          </p>
                                        </div>
                                      </div>
                                      <div className="space-y-4">
                                        {practice.providers.map(
                                          (provider: any, pIndex: number) => (
                                            <div
                                              key={pIndex}
                                              className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                                            >
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <FormField
                                                  control={control}
                                                  name={
                                                    `practices.${index}.providers.${pIndex}.name` as any
                                                  }
                                                  render={({ field }) => (
                                                    <FormItem>
                                                      <FormLabel className="text-sm font-medium text-gray-700">
                                                        Provider Name
                                                      </FormLabel>
                                                      <FormControl>
                                                        <Input
                                                          placeholder="e.g., Dr. Jane Smith, MD"
                                                          className="h-12 border-gray-200 focus:border-[#005151] focus:ring-[#005151] bg-white"
                                                          {...field}
                                                        />
                                                      </FormControl>
                                                      <FormMessage />
                                                    </FormItem>
                                                  )}
                                                />
                                                <FormField
                                                  control={control}
                                                  name={
                                                    `practices.${index}.providers.${pIndex}.npi` as any
                                                  }
                                                  render={({ field }) => (
                                                    <FormItem>
                                                      <FormLabel className="text-sm font-medium text-gray-700">
                                                        NPI Number
                                                      </FormLabel>
                                                      <FormControl>
                                                        <Input
                                                          placeholder="e.g., 1234567890"
                                                          className="h-12 border-gray-200 focus:border-[#005151] focus:ring-[#005151] bg-white"
                                                          {...field}
                                                        />
                                                      </FormControl>
                                                      <FormMessage />
                                                    </FormItem>
                                                  )}
                                                />
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                                <div className="border-t pt-4 mt-4 flex justify-between">
                                  {practice.id ? (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() =>
                                        setPracticeToArchive(practice)
                                      }
                                    >
                                      <Archive className="mr-2 h-4 w-4" />
                                      Archive Practice
                                    </Button>
                                  ) : (
                                    <span />
                                  )}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => {
                                      setSelectedPracticeId(practice.id);
                                      setShowNewProviderDialog(true);
                                    }}
                                  >
                                    <UserPlus className="mr-2 h-4 w-4" />
                                    Add New Provider
                                  </Button>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      )}
                    </CardContent>
                  </Card>

                  {/* Save Button */}
                  <div className="flex justify-center pt-8">
                    <Button
                      type="submit"
                      size="lg"
                      disabled={submitting}
                      className="bg-gradient-to-r from-[#005151] to-[#004040] hover:from-[#004040] hover:to-[#003030] text-white px-12 py-4 text-lg font-semibold shadow-lg"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save All Changes"
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>
        </div>
      </div>
      <Dialog
        open={!!practiceToArchive}
        onOpenChange={(open) => {
          if (!open && !archiving) setPracticeToArchive(null);
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Archive className="h-5 w-5 text-red-600" />
              <span>Archive {practiceToArchive?.name || "practice"}?</span>
            </DialogTitle>
            <DialogDescription>
              This practice and its providers will be removed from your active
              list and can no longer be selected on new requests. Past requests
              and their history are kept, but you won't be able to restore the
              practice yourself. Any unsaved edits on this page will be
              reloaded.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={archiving}
              onClick={() => setPracticeToArchive(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={archiving}
              onClick={handleArchivePractice}
            >
              {archiving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Archiving...
                </>
              ) : (
                "Archive Practice"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <NewPracticeDialog
        open={showNewProviderDialog}
        onOpenChange={setShowNewProviderDialog}
        practiceId={selectedPracticeId}
        refreshProfileInfo={() => {
          if (refreshProfileInfo) {
            refreshProfileInfo();
          }
          fetchInitialData();
        }}
      />
    </div>
  );
};

export default Account;
